import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { Breadcrumb } from "@shared/components";
import { CampoAvaliacao, CampoEditavelConsulta, CampoRevisavel, Consultation, ConsultationStatusEncerramentoEnum, CurrentConsultationResponse, GRAVIDADE_1_A_5, Transcription } from "../consultations.model";
import { ConsultationsService } from "../consultations.service";
import { ActivatedRoute } from "@angular/router";
import { NgBsToastService } from "ng-bs-toast-service";
import { HttpErrorResponse } from "@angular/common/http";
import { EMPTY, Subject, Subscription, catchError, forkJoin, switchMap, takeUntil, timer } from "rxjs";
import { NgBsModalService } from "ng-bs-modal-service";
import { ActiveConsultationSocketService } from "../active-consultation-socket.service";
import dayjs from "dayjs";

const REPROCESS_TRIGGER_FIELDS: CampoRevisavel[] = [
    "queixaPrincipal",
    "historiaDaDoenca",
    "antecedentesPessoaisFamiliares",
    "medicacaoUsoContinuo",
];
const REPROCESS_TIMEOUT_MS = 60_000;
const REPROCESS_POLL_INTERVAL_MS = 3_000;
const FEEDBACK_WINDOW_HOURS = 24;

@Component({
    selector: "app-view-consultation",
    standalone: false,
    templateUrl: "./view-consultation.component.html",
})
export class ViewConsultationComponent implements OnInit, OnDestroy {
    loading = { main: false, finishButton: false, feedback: false };
    consultation: Consultation;
    transcriptions: Transcription[];
    breadcrumb: Breadcrumb[] = [];
    reprocessing = false;

    private destroy$ = new Subject<void>();
    private reprocess$ = new Subject<void>();
    private reprocessTimeoutId: ReturnType<typeof setTimeout> | null = null;
    private reprocessBaseline: { hipoteses: string; conduta: string } | null = null;

    @ViewChild("feedbackFooter", { static: true }) feedbackFooter!: TemplateRef<any>;
    @ViewChild("feedbackBody", { static: true }) feedbackBody!: TemplateRef<any>;
    feedback: {
        consultationId: string;
        field: CampoEditavelConsulta;
        value: string;
    } = {
        consultationId: "",
        field: "" as CampoEditavelConsulta,
        value: "",
    };

    constructor(
        private activatedRoute: ActivatedRoute,
        private consultationsService: ConsultationsService,
        private toastService: NgBsToastService,
        private modalService: NgBsModalService,
        private socketService: ActiveConsultationSocketService,
    ) {}

    ngOnDestroy() {
        this.stopReprocessing();
        this.destroy$.next();
        this.destroy$.complete();
        this.reprocess$.complete();
    }

    ngOnInit() {
        const qp = this.activatedRoute.snapshot.queryParams;
        const backQueryParams: Record<string, string> = {};
        if (qp["startDate"]) backQueryParams["startDate"] = qp["startDate"];
        if (qp["endDate"]) backQueryParams["endDate"] = qp["endDate"];
        if (qp["doctorId"]) backQueryParams["doctorId"] = qp["doctorId"];
        if (qp["page"]) backQueryParams["page"] = qp["page"];

        this.breadcrumb = [
            { title: "Consultas", route: ["/consultations"], queryParams: backQueryParams },
            { title: "Detalhes da Consulta" },
        ];

        this.getConsultation();
    }

    get canEditFeedback() {
        if (!this.consultation?.finishedAt) return true;
        return dayjs().diff(dayjs(this.consultation.finishedAt), "hour", true) <= FEEDBACK_WINDOW_HOURS;
    }

    getConsultation() {
        this.loading.main = true;
        forkJoin([this.consultationsService.getById(this.activatedRoute.snapshot.params["id"]), this.consultationsService.getTranscription(this.activatedRoute.snapshot.params["id"])])
            .subscribe({
                next: ([consultation, transcription]) => {
                    this.consultation = consultation;
                    this.transcriptions = transcription;
                },
                error: (err: HttpErrorResponse) => this.toastService.send(`Error ${err.status}`, err.error.error || err.name, "danger"),
            })
            .add(() => (this.loading.main = false));
    }

    enviarFeedback() {
        if (this.loading.feedback) return;

        if (this.feedback.field === "exameFisico") {
            this.enviarExameFisico();
            return;
        }

        this.loading.feedback = true;
        const feedback = { ...this.feedback, field: this.feedback.field };
        const previousAvaliacao = this.consultation.avaliacoes?.[feedback.field];

        this.applyFeedbackExpectedValue(feedback.field, feedback.value);

        this.consultationsService.sendFeedback(feedback).subscribe({
            next: () => {
                this.modalService.close();
                if (this.isReprocessTriggerField(feedback.field)) {
                    this.toastService.send("Correção salva", "Reprocessando hipótese diagnóstica e conduta médica...", "primary");
                    this.startReprocessing();
                } else {
                    this.toastService.send("Feedback enviado", "Seu feedback foi enviado com sucesso.", "success");
                }
            },
            error: (err: HttpErrorResponse) => {
                this.restoreFeedbackExpectedValue(feedback.field, previousAvaliacao);
                this.toastService.send(`Error ${err.status}`, err.error?.error || err.name, "danger");
            },
        }).add(() => (this.loading.feedback = false));
    }

    private enviarExameFisico() {
        this.loading.feedback = true;
        const value = this.feedback.value.trim();
        const previousValue = this.consultation.exameFisico || "";

        this.consultation = {
            ...this.consultation,
            exameFisico: value,
        };

        this.consultationsService.savePhysicalExam(this.consultation.id, value).subscribe({
            next: () => {
                this.modalService.close();
                this.toastService.send("Exame físico salvo", "Os dados do exame físico foram enviados com sucesso.", "success");
            },
            error: (err: HttpErrorResponse) => {
                this.consultation = {
                    ...this.consultation,
                    exameFisico: previousValue,
                };
                this.toastService.send(`Error ${err.status}`, err.error?.error || err.name, "danger");
            },
        }).add(() => (this.loading.feedback = false));
    }

    private isReprocessTriggerField(field: CampoRevisavel) {
        return REPROCESS_TRIGGER_FIELDS.includes(field);
    }

    private startReprocessing() {
        if (this.reprocessing) {
            this.reprocess$.next();
            this.clearReprocessTimeout();
        }

        this.reprocessing = true;
        this.reprocessBaseline = {
            hipoteses: this.normalizeHipoteses(this.consultation.hipotesesDiagnostica),
            conduta: this.normalizeConduta(this.consultation.condutaMedica),
        };

        const consultationId = this.consultation.id;

        const socket$ = this.socketService.listenDiagnosis(consultationId).pipe(
            catchError(() => timer(0, REPROCESS_POLL_INTERVAL_MS).pipe(
                switchMap(() => this.consultationsService.getById(consultationId).pipe(
                    catchError(() => EMPTY),
                )),
            )),
        );

        socket$.pipe(
            takeUntil(this.destroy$),
            takeUntil(this.reprocess$),
        ).subscribe((payload) => this.handleReprocessedPayload(payload));

        this.reprocessTimeoutId = setTimeout(() => {
            if (!this.reprocessing) return;
            this.toastService.send(
                "Reprocessamento pendente",
                "A correção foi salva, mas o reprocessamento ainda não foi concluído. Atualize a página em alguns instantes.",
                "warning",
            );
            this.stopReprocessing();
        }, REPROCESS_TIMEOUT_MS);
    }

    private handleReprocessedPayload(payload: Partial<CurrentConsultationResponse> | Consultation) {
        if (!this.reprocessBaseline) return;

        const newHipoteses = this.normalizeHipoteses(payload.hipotesesDiagnostica);
        const newConduta = this.normalizeConduta(payload.condutaMedica);

        const hipotesesChanged = !!newHipoteses && newHipoteses !== this.reprocessBaseline.hipoteses;
        const condutaChanged = !!newConduta && newConduta !== this.reprocessBaseline.conduta;

        if (!hipotesesChanged && !condutaChanged) return;

        this.consultation = {
            ...this.consultation,
            ...(hipotesesChanged ? { hipotesesDiagnostica: this.toHipotesesArray(payload.hipotesesDiagnostica) } : {}),
            ...(condutaChanged ? { condutaMedica: this.toCondutaString(payload.condutaMedica) } : {}),
        };

        this.toastService.send("Reprocessamento concluído", "Nova hipótese diagnóstica e conduta médica atualizadas.", "success");
        this.stopReprocessing();
    }

    private stopReprocessing() {
        this.reprocess$.next();
        this.clearReprocessTimeout();
        this.reprocessing = false;
        this.reprocessBaseline = null;
    }

    private clearReprocessTimeout() {
        if (this.reprocessTimeoutId) {
            clearTimeout(this.reprocessTimeoutId);
            this.reprocessTimeoutId = null;
        }
    }

    private normalizeHipoteses(value?: string | string[] | null): string {
        if (!value) return "";
        return Array.isArray(value) ? value.join("\n").trim() : String(value).trim();
    }

    private normalizeConduta(value?: string | string[] | null): string {
        if (!value) return "";
        return Array.isArray(value) ? value.join("\n").trim() : String(value).trim();
    }

    private toHipotesesArray(value?: string | string[] | null): string[] {
        if (!value) return [];
        return Array.isArray(value) ? value : String(value).split("\n").filter((line) => line.trim().length > 0);
    }

    private toCondutaString(value?: string | string[] | null): string {
        if (!value) return "";
        return Array.isArray(value) ? value.join("\n") : String(value);
    }

    openModal(field: CampoEditavelConsulta) {
        if (!this.canEditFeedback) return;

        this.loading.feedback = false;
        this.feedback = {
            consultationId: this.consultation.id,
            value: this.getEditableFieldValue(field),
            field,
        };

        this.modalService.open(
            {
                header: "Feedback resposta Ariel",
                body: this.feedbackBody,
                footer: this.feedbackFooter,
            },
            { size: "lg" }
        );
    }

    getEditableFieldValue(field: CampoEditavelConsulta): string {
        if (field === "exameFisico") {
            return this.formatEditableValue(this.consultation.exameFisico);
        }

        const expectedValue = this.consultation.avaliacoes?.[field]?.expectedValue;
        if (expectedValue !== undefined && expectedValue !== null) {
            return this.formatEditableValue(expectedValue);
        }

        return this.formatEditableValue(this.consultation[field]);
    }

    private formatEditableValue(value?: string | string[] | null): string {
        if (Array.isArray(value)) {
            return value.join("\n");
        }

        return value ?? "";
    }

    private applyFeedbackExpectedValue(field: CampoRevisavel, value: string) {
        const currentAvaliacao = this.consultation.avaliacoes?.[field];

        this.consultation = {
            ...this.consultation,
            avaliacoes: {
                ...this.consultation.avaliacoes,
                [field]: {
                    ...currentAvaliacao,
                    hasErro: currentAvaliacao?.hasErro ?? true,
                    expectedValue: value,
                },
            },
        };
    }

    private restoreFeedbackExpectedValue(field: CampoRevisavel, previousAvaliacao?: CampoAvaliacao) {
        const avaliacoes = { ...this.consultation.avaliacoes };
        if (previousAvaliacao) {
            avaliacoes[field] = previousAvaliacao;
        } else {
            delete avaliacoes[field];
        }

        this.consultation = {
            ...this.consultation,
            avaliacoes,
        };
    }

    closeModal() {
        if (this.loading.feedback) return;

        this.modalService.close();
    }

    getScore(score: string) {
        const idx = Number(score);
        const config = idx ? GRAVIDADE_1_A_5[idx] : { title: "SEM TRIAGEM", color: "text-bg-light" };
        return `<span class="badge ${config.color}">${config.title}</span>`;
    }

    finishConsultation() {
        this.loading.finishButton = true;

        this.consultationsService
            .finish(this.activatedRoute.snapshot.params["id"])
            .subscribe({
                next: () => {
                    this.consultation.statusEncerramento = ConsultationStatusEncerramentoEnum.FINISHED;
                    this.toastService.send("Consulta finalizada", "A consulta foi finalizada com sucesso.", "success");
                },
                error: (err: HttpErrorResponse) => {
                    this.toastService.send(`Error ${err.status}`, err.error.error || err.name, "danger");
                },
            })
            .add(() => {
                this.loading.finishButton = false;
            });
    }

    limparTrecho(texto: string): string {
        return texto
            .replace(/\u00AD\s*/g, "")
            .replace(/[\u0080-\u009F]/g, "")
            .replace(/\s+/g, " ")
            .trim();
    }
}
